"use client";
import { useState } from "react";
import { Mascot } from "@/components/landing/Mascot";
import { Reveal } from "@/components/landing/Reveal";

const FAQS = [
  {
    q: "What exactly is a Surggie?",
    a: "A Surggie is a persistent AI agent assigned to a specific role in your business. Unlike a chatbot, a Surggie has memory, tools, and goals. It executes work autonomously — not just answers questions.",
  },
  {
    q: "Do I need to know how to code?",
    a: "Not at all. Surggies is built for founders and operators. You set up departments and agents through a visual interface. No coding required.",
  },
  {
    q: "What AI models do Surggies use?",
    a: "Surggies supports OpenAI (GPT-4o, GPT-4), Anthropic (Claude), and Groq models. You bring your own API keys (BYOK), so you're always in control and paying model providers directly.",
  },
  {
    q: "Is my data safe?",
    a: "Yes. With BYOK, your API calls go directly from your instance to the AI provider. We don't store your business data on our servers beyond what's needed for the app to function.",
  },
  {
    q: "How is this different from Zapier or n8n?",
    a: "Zapier and n8n are trigger-based automation tools. Surggies are proactive AI agents with goals and memory. They don't just react to triggers — they work toward objectives, learn from context, and take initiative.",
  },
  {
    q: "Is there a free plan?",
    a: "Yes. You can start free and spin up your first Surggie without a credit card. Paid plans unlock more agents, departments, and advanced tool integrations.",
  },
  {
    q: "What's the pixel RPG thing about?",
    a: "Your entire business HQ is rendered as a pixel-art game world. You walk through your departments, see your Surggies at their desks, and interact with your business in a way that's actually fun. It's optional eye candy — but once you see it, you can't go back.",
  },
  {
    q: "When is the full product launching?",
    a: "We're in early access now. Sign up to get your Surggies, help shape the product, and lock in early pricing before the public launch.",
  },
];

export function FAQSection() {
  const [open, setOpen] = useState<number | null>(null);

  return (
    <section id="faq" className="relative py-24 px-4 bg-cream">
      <div className="max-w-3xl mx-auto">
        <Reveal>
          <p className="text-center text-[var(--ink-soft)] font-mono text-xs uppercase tracking-widest mb-4">
            Questions
          </p>
          <div className="flex items-center justify-center gap-3 mb-12">
            <Mascot spriteKey="surggie_7" scale={3} />
            <h2
              className="text-center text-4xl md:text-5xl font-black text-[var(--ink)]"
              style={{ fontFamily: "var(--font-manrope)" }}
            >
              <span className="gold-shimmer">FAQ</span>
            </h2>
          </div>
        </Reveal>

        <div className="flex flex-col gap-4">
          {FAQS.map((faq, i) => (
            <Reveal key={i} delayMs={i * 40}>
              <div className="card-cute overflow-hidden transition-all duration-300">
                <button
                  className="w-full text-left p-6 flex items-center justify-between gap-4 hover:bg-[var(--gold)]/10 transition-colors"
                  onClick={() => setOpen(open === i ? null : i)}
                  aria-expanded={open === i}
                >
                  <span
                    className="font-bold text-[var(--ink)] text-sm md:text-base"
                    style={{ fontFamily: "var(--font-manrope)" }}
                  >
                    {faq.q}
                  </span>
                  <span
                    className={`flex-shrink-0 grid place-items-center w-8 h-8 rounded-full bg-gold border-2 border-[var(--ink)] text-[var(--ink)] font-mono text-xl leading-none transition-transform duration-300 ${
                      open === i ? "rotate-45" : ""
                    }`}
                  >
                    +
                  </span>
                </button>
                {open === i && (
                  <div className="px-6 pb-6">
                    <p className="text-[var(--ink-soft)] font-mono text-sm leading-relaxed border-t-2 border-[var(--gold-deep)]/30 pt-4">
                      {faq.a}
                    </p>
                  </div>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
